import { z } from "zod";

import {
  resolveOpenPencilSourceStructureSelection,
  type SourceStructureSelectionV1,
} from "./admission-v2.ts";

const contentRefSchema = z
  .object({
    id: z.string().min(1),
    contentHash: z.string().regex(/^sha256:[a-f0-9]{64}$/),
  })
  .strict();

const twoContentRefsSchema = z.array(contentRefSchema).length(2);

type ComponentSetFact = {
  name?: unknown;
  pageName?: unknown;
  variantCount?: unknown;
};

type ComponentReadback = {
  uiOntology?: {
    componentSets?: ComponentSetFact[];
  };
};

export interface OpenPencilComponentOntologyCandidateV1 {
  componentRoles: Array<{
    sourceName: string;
    canonicalRole: "button";
  }>;
  variants: Array<{
    component: string;
    observedVariantCount: number;
  }>;
  sourceToCogoRoleMappings: Array<{
    sourceRole: string;
    cogoRole: "button";
  }>;
  sourceRefs: Array<{ id: string; contentHash: string }>;
  confidence: "observed-structure";
}

function normalizedName(value: string): string {
  return value.normalize("NFKC").trim();
}

export function buildOpenPencilComponentOntologyCandidate(
  readback: unknown,
  selection: SourceStructureSelectionV1,
  sourceRefs: Array<{ id: string; contentHash: string }>,
): OpenPencilComponentOntologyCandidateV1 {
  const componentReadback = readback as ComponentReadback;
  let resolution;
  try {
    resolution = resolveOpenPencilSourceStructureSelection(
      componentReadback,
      selection,
    );
  } catch (error) {
    throw new Error(
      "ONTOLOGY_CANDIDATE_SELECTION_RESOLUTION_FAILED: selection resolution failed",
      { cause: error },
    );
  }
  if (!resolution.passed) {
    throw new Error(
      `ONTOLOGY_CANDIDATE_SELECTION_RESOLUTION_FAILED: selection resolution failed: ${resolution.errors.join("; ")}`,
    );
  }

  const refs = twoContentRefsSchema.safeParse(sourceRefs);
  if (!refs.success) {
    throw new Error(
      "ONTOLOGY_CANDIDATE_REQUIRES_EXACTLY_TWO_CLOSED_CONTENT_REFS: exactly two closed content refs are required",
    );
  }

  if (
    selection.members.length !== 1 ||
    selection.members[0]!.structuralPath.length !== 3 ||
    selection.members[0]!.structuralPath[1] !== "componentSets"
  ) {
    throw new Error("ONTOLOGY_CANDIDATE_REQUIRES_ONE_COMPONENT_SET_SELECTION");
  }

  const [selectedPage, , selectedName] =
    selection.members[0]!.structuralPath;
  const componentSets = componentReadback.uiOntology?.componentSets ?? [];
  const matches = componentSets.filter(
    (fact) =>
      typeof fact.pageName === "string" &&
      typeof fact.name === "string" &&
      normalizedName(fact.pageName) === normalizedName(selectedPage!) &&
      normalizedName(fact.name) === normalizedName(selectedName!),
  );
  if (matches.length !== 1) {
    throw new Error("ONTOLOGY_CANDIDATE_COMPONENT_SET_MUST_RESOLVE_EXACTLY_ONCE");
  }

  const selectedFact = matches[0]!;
  const sourceName = normalizedName(selectedFact.name as string);
  if (sourceName.toLocaleLowerCase("en-US") !== "button") {
    throw new Error(
      "ONTOLOGY_CANDIDATE_SOURCE_ROLE_NOT_IN_CLOSED_SOURCE_ROLE_MAPPING: source role is outside the closed source role mapping",
    );
  }
  const observedVariantCount = selectedFact.variantCount;
  if (
    typeof observedVariantCount !== "number" ||
    !Number.isInteger(observedVariantCount) ||
    observedVariantCount <= 0
  ) {
    throw new Error(
      "ONTOLOGY_CANDIDATE_OBSERVED_VARIANT_COUNT_MUST_BE_A_POSITIVE_INTEGER: observed variant count must be a positive integer",
    );
  }

  return {
    componentRoles: [{ sourceName, canonicalRole: "button" }],
    variants: [
      {
        component: sourceName,
        observedVariantCount,
      },
    ],
    sourceToCogoRoleMappings: [
      { sourceRole: sourceName, cogoRole: "button" },
    ],
    sourceRefs: refs.data,
    confidence: "observed-structure",
  };
}
